/*
 * The running event, resolved once at boot from GET /event/current.
 *
 * Three facts about the event used to live in the client and could each contradict the
 * API: which event it is, its name/venue/dates, and the certificate threshold. They had
 * already drifted — the database called event 6 "Continuing Professional Development
 * 2026" while the client printed "Kursus Pembangunan Profesional Berterusan (CPD) PK
 * UiTM 2026" — and nothing could have noticed. `event.is_current` decides now, and this
 * module is the only place the answer lives.
 *
 * ## There is no fallback, on purpose
 *
 * An earlier version of this file kept a hard-coded event to fall back on when the call
 * failed. That was the same mistake in a smaller box: the app would have carried on
 * showing an event's name, dates and certificate rule taken from a copy that nothing
 * keeps in step — which is exactly how the two names drifted apart.
 *
 * So a live build that cannot reach the server has NO event, and says so. main.tsx
 * renders an explicit "cannot reach the event server" screen instead of the app. That is
 * a worse-looking failure and a much better one: nobody reads a stale event name and
 * believes it.
 *
 * DEMO_EVENT below is not a fallback. Demo mode has no server by definition — that is
 * what it is for — so the fixtures have to describe some event, and this is it.
 */

export interface EventConfig {
  id: number
  name: string
  shortName: string
  organizer: string
  venue: string
  startDate: string
  endDate: string
  /** Percent, not a fraction. The API converts; nothing here has to know the unit. */
  certificateAttendanceThreshold: number
  /** Acoustic check-in is offered beside the camera. Organiser toggle, off by default. */
  soundCheckin: boolean
}

/*
 * The event `mock/fixtures.ts` depicts.
 *
 * Used ONLY when the app is running on fixtures. It is demo data that happens to be
 * shaped like a config, not a spare copy of the real event — nothing reads it in a live
 * build, and `fixtures.ts` asserts its programme against these dates so the two cannot
 * drift.
 */
export const DEMO_EVENT: EventConfig = {
  id: 6,
  name: 'Kursus Pembangunan Profesional Berterusan (CPD) PK UiTM 2026',
  shortName: 'CPD 2026',
  organizer: 'Universiti Teknologi MARA',
  venue: 'ILD UiTM Bandar Enstek, Nilai, Negeri Sembilan',
  startDate: '2026-08-24',
  endDate: '2026-08-26',
  certificateAttendanceThreshold: 70,
  soundCheckin: true,
}

/*
 * Null until boot resolves it. Every `EVENT.x` read happens inside a render or a
 * handler, and main.tsx does not render the app until this is set — so `eventConfig()`
 * throwing means a genuine ordering bug, not a network problem.
 */
let current: EventConfig | null = null

export function eventConfig(): EventConfig {
  if (!current) {
    throw new Error(
      'The event config was read before boot resolved it. main.tsx must await ' +
      'loadEventConfig (or call useDemoEvent) before rendering.',
    )
  }
  return current
}

/*
 * Keep the boot-resolved threshold in step after an organiser saves a new one.
 *
 * `EVENT.certificateAttendanceThreshold` is resolved once at boot, so a saved figure
 * would otherwise sit alongside a stale module value for the rest of the session. The
 * only live reader is Schedule.tsx — a participant screen, which an organiser account
 * never opens — so this changes nothing an organiser can see today; it matters in demo
 * mode, where one browser is both roles, and it stops the next reader of this module
 * from quoting a number the server no longer holds.
 *
 * It is not a subscription. EVENT is a getter over module state, not a store, so a
 * component already on screen does not re-render on this. Delegates get the new figure
 * on their next load; the settings screen says so.
 *
 * It mutates rather than re-fetching: the value just came from the server's own reply.
 */
export function applyCertificateThreshold(percent: number): void {
  if (current) current = { ...current, certificateAttendanceThreshold: percent }
}

/** Same idea for the acoustic toggle: the organiser's other screens see it at once. */
export function applySoundCheckin(on: boolean): void {
  if (current) current = { ...current, soundCheckin: on }
}

export function isEventResolved(): boolean {
  return current !== null
}

/** Demo mode: pin the config to the event the fixtures describe. */
export function useDemoEvent(): EventConfig {
  current = DEMO_EVENT
  return current
}

interface EventCurrentResponse {
  ok: boolean
  event: null | {
    id: number
    name: string
    short_name: string | null
    organizer: string | null
    venue: string | null
    start_date: string
    end_date: string
    certificate_threshold: number | null
    sound_checkin?: boolean
  }
}

/**
 * Fetch the running event.
 *
 * Returns null rather than inventing one — on a timeout, an offline server, a malformed
 * reply, or a database with nothing flagged current. The caller decides what to do with
 * that, and in a live build the answer is to say so rather than to render.
 */
export async function loadEventConfig(base: string, timeoutMs = 2500): Promise<EventConfig | null> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), timeoutMs)
  try {
    const res = await fetch(`${base}/event/current`, { signal: abort.signal })
    if (!res.ok) return null
    const body = (await res.json()) as EventCurrentResponse
    if (!body?.ok || !body.event) return null

    const e = body.event
    if (!Number.isInteger(Number(e.id)) || Number(e.id) < 1) return null

    current = {
      id: Number(e.id),
      name: e.name,
      /* The full name rather than a blank: this is printed on the delegate pass. */
      shortName: e.short_name || e.name,
      organizer: e.organizer || '',
      venue: e.venue || '',
      startDate: e.start_date,
      endDate: e.end_date,
      /*
       * `??` and not `||`: a threshold of 0 is a real setting meaning every delegate
       * qualifies, and `||` would silently turn it into 70. Null means the event has no
       * setting_apps row, which is its own answer — 100 rather than a guess, so nothing
       * is reported as eligible on the strength of a missing row.
       *
       * 100 is a floor, not a rule anyone is stuck with: the organiser's Settings screen
       * writes this figure, and saving it on an event with no row creates the row.
       */
      certificateAttendanceThreshold: e.certificate_threshold ?? 100,
      soundCheckin: Boolean(e.sound_checkin),
    }
    return current
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}
